import { useContext } from "react"

import { DragOriginContext } from "shared/context/drag-and-drop/DragOriginContext"
import { DropTarget } from "shared/types/drag-and-drop/DropTarget"

import {
  DragEndEvent,
  UseDroppableArguments,
  useDroppable as useDndKitDroppable,
  useDndMonitor
} from "@dnd-kit/core"

interface UseDroppableArgsBase<Data = unknown>
  extends Omit<UseDroppableArguments, "data"> {
  data: Data
  type: string
  accept?: string[]
  sameOrigin?: boolean
  error?: boolean
  onDragEnd?: (event: DragEndEvent) => void
}

export type UseDroppableArgs<
  Data = unknown,
  Meta = unknown
> = unknown extends Meta
  ? UseDroppableArgsBase<Data> & { meta?: Meta }
  : UseDroppableArgsBase<Data> & { meta: Meta }

export function useDroppable<Data = unknown, Meta = unknown>({
  id,
  data,
  meta,
  disabled,
  type,
  sameOrigin,
  error,
  onDragEnd
}: UseDroppableArgs<Data, Meta>) {
  const origin = useContext(DragOriginContext)
  const dropTarget: DropTarget<Data, Meta | undefined> = {
    id: id.toString(),
    meta,
    origin,
    data,
    sameOrigin,
    error,
    type
  }

  useDndMonitor({
    onDragEnd: event => {
      const { over } = event
      if (over?.id !== id) return
      onDragEnd?.(event)
    }
  })

  return useDndKitDroppable({
    id,
    data: dropTarget,
    disabled
  })
}
